import React, { ReactNode } from 'react';

import PropTypes from 'prop-types';

import { CheckboxState } from '@adsk/alloy-react-checkbox';
import Divider from '@adsk/alloy-react-divider';
import { getValidChildrenCount } from '@adsk/alloy-react-helpers';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import Option from './layout/FooterOption';

type CheckboxOnChange = (
  state: CheckboxState,
  event: React.ChangeEvent<HTMLInputElement>,
) => void;

type CheckboxOption = {
  label: ReactNode;
  defaultChecked?: boolean;
  onChange: CheckboxOnChange;
};

export type FooterProps = StylableComponent<HTMLDivElement> & {
  options?: CheckboxOption[];
};

const Footer = ({ style, children, options = [], ...props }: FooterProps) => {
  const hasMultipleChildren = getValidChildrenCount(children) > 1;

  return (
    <div css={[style]} {...props}>
      <Divider />
      {options.length > 0 && (
        <div
          css={[
            {
              padding: '0px 8px',
              background: theme.tokens.colors.background.secondary.value,
            },
          ]}
        >
          {options.map((option, i) => (
            <div
              key={i}
              css={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 8px',
              }}
            >
              <Option {...option} />
            </div>
          ))}
        </div>
      )}
      <div
        css={{
          display: 'flex',
          justifyContent: hasMultipleChildren ? 'space-between' : 'right',
          alignItems: 'center',
          padding: '0px 16px',
          height: 48,
        }}
      >
        {children}
      </div>
    </div>
  );
};

Footer.displayName = 'Footer';

Footer.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Content that will be displayed in the main (lower) area of the Footer */
  children: PropTypes.any,
  /** Optional props to set on the Clear button */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      defaultChecked: PropTypes.bool,
      onChange: PropTypes.func.isRequired,
    }),
  ),
};

export default Footer;
